/*
 * Keeps the product page sale badge in sync with the selected subscription plan.
 *
 * `snippets/price.liquid` renders the badge from the variant's one-time pricing and
 * exposes the variant's selling plan prices on the badge element. The Recharge widget
 * only rewrites the price text, so the discount shown on the badge is recalculated
 * here whenever a selling plan is selected, changed or cleared.
 */
(() => {
  if (window.subscriptionSaleBadge) return;

  const BADGE_SELECTOR = '[data-sale-badge]';
  const SELLING_PLAN_SELECTOR = '[name="selling_plan"]';
  const EN_DASH = '\u2013';

  function formatMoney(cents, format) {
    if (window.theme?.Currency?.formatMoney) {
      return theme.Currency.formatMoney(cents, format);
    }

    return `${(cents / 100).toFixed(2)}`;
  }

  function getSellingPlanPrices(badge) {
    try {
      return JSON.parse(badge.dataset.sellingPlanPrices || '{}');
    } catch (error) {
      console.error('Sale badge: could not read selling plan prices', error);
      return {};
    }
  }

  function getSelectedSellingPlanId(root) {
    const inputs = Array.from(root.querySelectorAll(SELLING_PLAN_SELECTOR));
    const selected = inputs.find((input) => input.value);

    return selected ? selected.value : null;
  }

  function getBadgeText(badge, price) {
    const compareAtPrice = Number(badge.dataset.compareAtPrice);
    const discount = compareAtPrice - price;

    if (discount <= 0) return null;

    switch (badge.dataset.saleBadgeBasis) {
      case 'value':
        return `${EN_DASH}${formatMoney(discount, badge.dataset.moneyFormat)}`;
      case 'percentage':
        return `${EN_DASH}${Math.round((discount * 100) / compareAtPrice)}%`;
      default:
        return null;
    }
  }

  function updateBadge(badge) {
    const oneTimePrice = Number(badge.dataset.price);
    if (Number.isNaN(oneTimePrice)) return;

    const root = badge.closest('product-info, .shopify-section') || document;
    const sellingPlanId = getSelectedSellingPlanId(root);
    const sellingPlanPrices = getSellingPlanPrices(badge);
    const price = sellingPlanId in sellingPlanPrices ? sellingPlanPrices[sellingPlanId] : oneTimePrice;
    const text = getBadgeText(badge, price);

    if (text === null || badge.textContent.trim() === text) return;

    badge.textContent = text;
  }

  let scheduledUpdate = null;

  function scheduleUpdate() {
    if (scheduledUpdate) return;

    scheduledUpdate = requestAnimationFrame(() => {
      scheduledUpdate = null;
      document.querySelectorAll(BADGE_SELECTOR).forEach(updateBadge);
    });
  }

  function observeProductInfo() {
    // The subscription widget is rendered by an app, so its selection is tracked
    // through the DOM changes it makes instead of app specific events.
    const observer = new MutationObserver(scheduleUpdate);

    document.querySelectorAll('product-info').forEach((productInfo) => {
      observer.observe(productInfo, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['name', 'value', 'class']
      });
    });
  }

  window.subscriptionSaleBadge = { update: scheduleUpdate };

  document.addEventListener('change', scheduleUpdate);
  document.addEventListener('variant:change', scheduleUpdate);
  document.addEventListener('shopify:section:load', () => {
    observeProductInfo();
    scheduleUpdate();
  });

  observeProductInfo();
  scheduleUpdate();
})();
